import { Link, useLocation } from 'react-router-dom';

type Tone = 'up' | 'down' | 'broken';

const CANDLES: { h: number; tone: Tone }[] = [
  { h: 24, tone: 'up' },
  { h: 32, tone: 'down' },
  { h: 41, tone: 'up' },
  { h: 36, tone: 'down' },
  { h: 48, tone: 'up' },
  { h: 57, tone: 'up' },
  { h: 50, tone: 'down' },
  { h: 62, tone: 'up' },
  { h: 71, tone: 'up' },
  { h: 64, tone: 'down' },
  { h: 79, tone: 'up' },
  { h: 90, tone: 'up' },
  { h: 74, tone: 'down' },
  { h: 84, tone: 'up' },
  { h: 66, tone: 'broken' },
  { h: 52, tone: 'broken' },
  { h: 40, tone: 'broken' },
];

const ROUTES = [
  {
    code: 'HOME',
    to: '/',
    icon: 'ri-home-5-line',
    title: 'Home',
    desc: 'Track record, the strategy story and where the EA fits your desk.',
  },
  {
    code: 'PERF',
    to: '/performance',
    icon: 'ri-line-chart-line',
    title: 'Live Performance',
    desc: 'Verified monthly results, drawdown analysis and recent trades.',
  },
  {
    code: 'HOWTO',
    to: '/how-it-works',
    icon: 'ri-flow-chart',
    title: 'How It Works',
    desc: 'Strategy logic, session windows and the risk engine, explained.',
  },
  {
    code: 'GETEA',
    to: '/get-ea',
    icon: 'ri-download-2-line',
    title: 'Get EA Free',
    desc: 'Download the MT5 expert advisor and check setup requirements.',
  },
  {
    code: 'FAQ',
    to: '/faq',
    icon: 'ri-question-answer-line',
    title: 'FAQ',
    desc: 'Brokers, VPS, risk presets and version history — all answered.',
  },
];

function candleClass(tone: Tone): string {
  if (tone === 'up') return 'bg-primary-400/85';
  if (tone === 'down') return 'bg-accent-500/90';
  return 'border border-dashed border-accent-400/50 bg-transparent';
}

export default function NotFound() {
  const location = useLocation();

  return (
    <div className="bg-background-50">
      {/* Terminal hero */}
      <section className="relative pt-24 md:pt-32 pb-14 md:pb-20 overflow-hidden">
        <div
          className="absolute -right-32 top-10 w-96 h-96 rounded-full bg-accent-200/40 blur-3xl"
          aria-hidden="true"
        ></div>
        <div
          className="absolute -left-32 bottom-0 w-80 h-80 rounded-full bg-primary-100/50 blur-3xl"
          aria-hidden="true"
        ></div>

        <div className="relative w-full px-4 md:px-8">
          <div className="max-w-shell mx-auto">
            <div className="animate-fade-up overflow-hidden rounded-lg border border-primary-900/40 bg-primary-950 shadow-none">
              {/* Window chrome */}
              <div className="flex items-center gap-2 border-b border-background-50/10 bg-background-50/[0.05] px-4 md:px-5 py-3">
                <span className="w-2.5 h-2.5 rounded-full bg-accent-500/80" aria-hidden="true"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-secondary-500/60" aria-hidden="true"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-primary-400/70" aria-hidden="true"></span>
                <span className="ml-2 font-mono text-[11px] tracking-wide text-background-200/55">
                  goldwarden &middot; terminal
                </span>
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" aria-hidden="true"></span>
                  no route
                </span>
              </div>

              {/* Terminal body */}
              <div
                className="relative px-5 md:px-10 py-8 md:py-12"
                style={{
                  backgroundImage:
                    'linear-gradient(oklch(var(--background-50) / 0.04) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--background-50) / 0.04) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                }}
              >
                <div className="font-mono text-xs md:text-sm text-background-200/70">
                  <span className="text-accent-400">$</span> resolve --route{' '}
                  <span className="text-background-100 break-all">{location.pathname}</span>
                </div>
                <div className="mt-2 font-mono text-xs md:text-sm">
                  <span className="font-semibold text-accent-400">ERR_404</span>
                  <span className="text-background-200/70"> &middot; route not found on this exchange</span>
                </div>

                {/* Giant 404 */}
                <div
                  className="mt-7 flex items-end font-mono text-[80px] md:text-[148px] font-bold leading-[0.8] tracking-tight text-accent-400 select-none"
                  aria-hidden="true"
                >
                  404
                  <span className="ml-1 mb-2 md:mb-4 w-[10px] md:w-[16px] h-[60px] md:h-[110px] bg-accent-500 animate-pulse-dot rounded-sm"></span>
                </div>

                <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50 max-w-2xl text-balance">
                  Signal lost — this route isn&apos;t on the chart
                </h1>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-background-200/75 max-w-xl">
                  This ticker came back empty. The link may have moved, been renamed, or have a
                  small typo in it. No capital lost — the rest of GoldWarden is still trading as usual.
                </p>

                {/* Broken chart */}
                <div className="mt-8 rounded-lg border border-background-50/10 bg-background-50/[0.03] p-4 md:p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-background-200/55">
                      XAUUSD &middot; last known price action
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-400">
                      data feed interrupted
                    </span>
                  </div>
                  <div className="flex items-end gap-1.5 md:gap-2 h-24 md:h-28">
                    {CANDLES.map((candle, index) => (
                      <span
                        key={index}
                        className={`flex-1 rounded-sm ${candleClass(candle.tone)}`}
                        style={{ height: `${candle.h}%` }}
                      ></span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-400 px-6 py-3 text-sm font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
                    Back to Home
                  </Link>
                  <Link
                    to="/get-ea"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-background-50/25 hover:bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-2-line text-base" aria-hidden="true"></i>
                    Get the EA Free
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery routes */}
      <section className="pb-16 md:pb-24">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-shell mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-7">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-secondary-500">
                  Recovery routes
                </span>
                <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  Pick a route and get back on the chart
                </h2>
              </div>
              <p className="text-sm text-foreground-600 max-w-md">
                The five destinations traders land on most — one tap away.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
              {/* Route watchlist */}
              <div className="lg:col-span-2 flex flex-col rounded-lg border border-background-200 bg-background-100 p-2">
                {ROUTES.map((route) => (
                  <Link
                    key={route.to}
                    to={route.to}
                    className="group flex items-center gap-3 md:gap-4 rounded-lg px-3 md:px-4 py-3.5 transition-colors hover:bg-background-50 cursor-pointer"
                  >
                    <span className="w-14 shrink-0 font-mono text-[11px] font-semibold tracking-wider text-secondary-500">
                      {route.code}
                    </span>
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 transition-colors group-hover:bg-accent-500 group-hover:text-primary-950">
                      <i className={`${route.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 font-heading text-sm md:text-base font-semibold text-foreground-950">
                        {route.title}
                        <i
                          className="ri-arrow-right-up-line text-sm text-secondary-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          aria-hidden="true"
                        ></i>
                      </span>
                      <span className="mt-0.5 block text-xs md:text-sm leading-relaxed text-foreground-600 truncate">
                        {route.desc}
                      </span>
                    </span>
                    <i
                      className="ri-arrow-right-line hidden sm:block text-lg text-secondary-300 transition-all group-hover:text-accent-600 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    ></i>
                  </Link>
                ))}
              </div>

              {/* Support panel */}
              <div className="flex flex-col rounded-lg bg-primary-950 p-6 overflow-hidden relative">
                <div
                  className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent-500/15 blur-2xl"
                  aria-hidden="true"
                ></div>
                <div className="relative flex-1">
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500 text-primary-950">
                    <i className="ri-customer-service-2-line text-xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-background-50">
                    Still off the chart?
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-200/75">
                    Tell us what you were looking for and we&apos;ll point you to the right page or
                    resource.
                  </p>
                </div>
                <Link
                  to="/faq"
                  className="relative mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-400 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit the FAQ
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}